import React, { useState, useEffect } from 'react';
import { ApiClient } from '../../api/client';
import type { ActivityAnalyticsData, Tenant } from '../../types';
import {
  BarChart3, RefreshCw, Building2, Users,
  Activity, HardDrive, FileText, Sparkles, Clock
} from 'lucide-react';

interface ActivityAnalyticsViewProps {
  tenants: Tenant[];
  initialTenantId?: string;
}

export const ActivityAnalyticsView: React.FC<ActivityAnalyticsViewProps> = ({
  tenants,
  initialTenantId = 'all',
}) => {
  const [data, setData] = useState<ActivityAnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [daysFilter, setDaysFilter] = useState<string>('30');
  const [tenantFilter, setTenantFilter] = useState<string>(initialTenantId);

  const loadAnalytics = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await ApiClient.getActivityAnalytics({
        days: daysFilter,
        tenant_id: tenantFilter !== 'all' ? tenantFilter : undefined,
      });
      if (res.ok && res.data) {
        setData(res.data);
      } else {
        setError(res.error || 'Nem sikerült betölteni az analitikai adatokat.');
      }
    } catch (e: any) {
      setError(e.message || 'Hiba történt a statisztika lekérése során.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAnalytics();
  }, [daysFilter, tenantFilter]);

  const getActionColor = (action: string) => {
    const a = action.toLowerCase();
    if (a.includes('download')) return 'bg-blue-500 text-blue-700 dark:text-blue-300';
    if (a.includes('preview') || a.includes('view')) return 'bg-indigo-500 text-indigo-700 dark:text-indigo-300';
    if (a.includes('upload') || a.includes('create') || a.includes('add')) return 'bg-emerald-500 text-emerald-700 dark:text-emerald-300';
    if (a.includes('update') || a.includes('edit')) return 'bg-amber-500 text-amber-700 dark:text-amber-300';
    if (a.includes('delete')) return 'bg-rose-500 text-rose-700 dark:text-rose-300';
    if (a.includes('login') || a.includes('logout')) return 'bg-cyan-500 text-cyan-700 dark:text-cyan-300';
    if (a.includes('impersonate')) return 'bg-purple-500 text-purple-700 dark:text-purple-300';
    return 'bg-slate-500 text-slate-700 dark:text-slate-300';
  };

  return (
    <div className="space-y-6">
      {/* Szűrősáv & Frissítés */}
      <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-4 shadow-xs flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <div className="p-2 bg-indigo-50 dark:bg-indigo-950/60 text-indigo-600 dark:text-indigo-400 rounded-xl">
            <BarChart3 className="w-5 h-5" />
          </div>
          <div>
            <h2 className="text-base font-bold text-slate-900 dark:text-white">Műveleti Gyakoriság & Aktivitás-hőtérkép</h2>
            <p className="text-xs text-slate-500 dark:text-slate-400">Rendszerhasználati statisztika és kattintási eloszlás a továbbfejlesztés támogatásához</p>
          </div>
        </div>

        <div className="flex items-center gap-2.5 flex-wrap">
          {/* Időszak választó */}
          <div className="flex items-center gap-1 bg-slate-100 dark:bg-slate-800/80 p-1 rounded-xl border border-slate-200 dark:border-slate-700/80">
            {[
              { id: '7', label: '7 nap' },
              { id: '30', label: '30 nap' },
              { id: '90', label: '90 nap' },
              { id: 'all', label: 'Mind' },
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setDaysFilter(tab.id)}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition cursor-pointer ${
                  daysFilter === tab.id
                    ? 'bg-white dark:bg-slate-900 text-indigo-600 dark:text-cyan-400 shadow-xs'
                    : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Szervezet szűrő */}
          <select
            value={tenantFilter}
            onChange={(e) => setTenantFilter(e.target.value)}
            className="px-3 py-2 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl text-xs font-semibold text-slate-800 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            <option value="all">Minden Intézmény / Szervezet</option>
            {tenants.map(t => (
              <option key={t.id} value={String(t.id)}>{t.name}</option>
            ))}
          </select>

          {/* Frissítés gomb */}
          <button
            onClick={loadAnalytics}
            disabled={loading}
            className="p-2 rounded-xl border border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-600 dark:text-slate-300 transition cursor-pointer disabled:opacity-50"
            title="Adatok frissítése"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {error && (
        <div className="p-4 bg-rose-50 dark:bg-rose-950/40 border border-rose-200 dark:border-rose-900 rounded-2xl text-xs text-rose-700 dark:text-rose-300">
          {error}
        </div>
      )}

      {/* KPI Kártyák */}
      {data && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-4 shadow-xs">
            <div className="flex items-center justify-between text-slate-400 mb-2">
              <span className="text-xs font-semibold">Összes Művelet</span>
              <Activity className="w-4 h-4 text-indigo-500" />
            </div>
            <div className="text-2xl font-black text-slate-900 dark:text-white font-mono">
              {data.summary.total_actions.toLocaleString()}
            </div>
            <p className="text-[11px] text-slate-500 mt-1">a kiválasztott időszakban</p>
          </div>

          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-4 shadow-xs">
            <div className="flex items-center justify-between text-slate-400 mb-2">
              <span className="text-xs font-semibold">Mai Műveletek</span>
              <Clock className="w-4 h-4 text-emerald-500" />
            </div>
            <div className="text-2xl font-black text-emerald-600 dark:text-emerald-400 font-mono">
              {data.summary.actions_today.toLocaleString()}
            </div>
            <p className="text-[11px] text-slate-500 mt-1">ma éjfél óta rögzítve</p>
          </div>

          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-4 shadow-xs">
            <div className="flex items-center justify-between text-slate-400 mb-2">
              <span className="text-xs font-semibold">Aktív Felhasználók</span>
              <Users className="w-4 h-4 text-blue-500" />
            </div>
            <div className="text-2xl font-black text-slate-900 dark:text-white font-mono">
              {data.summary.unique_users} <span className="text-xs font-normal text-slate-400">fő</span>
            </div>
            <p className="text-[11px] text-slate-500 mt-1">műveletet végző személyek</p>
          </div>

          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-4 shadow-xs">
            <div className="flex items-center justify-between text-slate-400 mb-2">
              <span className="text-xs font-semibold">Érintett Intézmények</span>
              <Building2 className="w-4 h-4 text-purple-500" />
            </div>
            <div className="text-2xl font-black text-slate-900 dark:text-white font-mono">
              {data.summary.active_tenants} <span className="text-xs font-normal text-slate-400">szervezet</span>
            </div>
            <p className="text-[11px] text-slate-500 mt-1">rendszert használó bérlők</p>
          </div>

          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-4 shadow-xs">
            <div className="flex items-center justify-between text-slate-400 mb-2">
              <span className="text-xs font-semibold">Top Tevékenység</span>
              <Sparkles className="w-4 h-4 text-amber-500" />
            </div>
            <div className="text-sm font-bold text-slate-900 dark:text-white truncate" title={data.summary.top_action?.label || '–'}>
              {data.summary.top_action?.label || '–'}
            </div>
            <div className="text-xs text-amber-600 dark:text-amber-400 font-mono font-semibold mt-1">
              {data.summary.top_action ? `${data.summary.top_action.count} db (${((data.summary.top_action.count / (data.summary.total_actions || 1)) * 100).toFixed(0)}%)` : '–'}
            </div>
          </div>
        </div>
      )}

      {/* Műveleti Megoszlás & Napi Idővonal */}
      {data && (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Bal oszlop: Műveletek gyakorisági sávjai (7 oszlop) */}
          <div className="lg:col-span-7 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-5 shadow-xs space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-sm font-bold text-slate-900 dark:text-white flex items-center gap-2">
                  <span>Műveletek Gyakorisága & Kattintási Rangsor</span>
                  <span className="px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-800 text-[10px] font-mono text-slate-600 dark:text-slate-400">
                    {data.action_counts.length} féle akció
                  </span>
                </h3>
                <p className="text-xs text-slate-500">Mely funkciókra kattintanak a leggyakrabban a felhasználók?</p>
              </div>
            </div>

            <div className="space-y-3 max-h-[460px] overflow-y-auto pr-2">
              {data.action_counts.map((item, idx) => {
                const colorCls = getActionColor(item.action);
                const bgBarColor = colorCls.split(' ')[0];
                return (
                  <div key={item.action} className="p-2.5 rounded-xl bg-slate-50 dark:bg-slate-800/50 border border-slate-200/60 dark:border-slate-800 hover:border-indigo-300 transition">
                    <div className="flex items-center justify-between text-xs mb-1.5">
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="font-mono text-slate-400 text-[11px] w-5 text-right font-bold">{idx + 1}.</span>
                        <span className="font-bold text-slate-800 dark:text-slate-100 truncate">{item.label}</span>
                        <span className="text-[10px] font-mono text-slate-400 truncate hidden sm:inline">({item.action})</span>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <span className="font-mono font-bold text-slate-900 dark:text-white">{item.count} db</span>
                        <span className="text-[11px] font-semibold text-indigo-600 dark:text-indigo-400 w-11 text-right">{item.percentage}%</span>
                      </div>
                    </div>
                    {/* Progress Bar */}
                    <div className="w-full h-2 bg-slate-200 dark:bg-slate-700/60 rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all duration-500 ${bgBarColor}`}
                        style={{ width: `${Math.max(2, item.percentage)}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Jobb oszlop: Napi Trend & Óránkénti csúcs (5 oszlop) */}
          <div className="lg:col-span-5 space-y-6">
            {/* Napi aktivitási trend */}
            <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-5 shadow-xs space-y-3">
              <h3 className="text-sm font-bold text-slate-900 dark:text-white flex items-center justify-between">
                <span>Napi Aktivitási Trend</span>
                <span className="text-xs font-normal text-slate-400">Utolsó 30 nap</span>
              </h3>
              <p className="text-xs text-slate-500">Műveletek időbeli eloszlása naponta</p>

              <div className="pt-2">
                {data.timeline.length === 0 ? (
                  <div className="text-xs text-slate-400 py-6 text-center">Nincs elérhető idővonal adat.</div>
                ) : (
                  <div className="flex items-end gap-1 h-32 pt-4 border-b border-slate-200 dark:border-slate-800">
                    {(() => {
                      const maxDay = Math.max(...data.timeline.map(t => t.count), 1);
                      return data.timeline.map(t => {
                        const pct = Math.round((t.count / maxDay) * 100);
                        return (
                          <div
                            key={t.date}
                            className="flex-1 flex flex-col items-center group relative h-full justify-end"
                          >
                            {/* Tooltip */}
                            <div className="absolute -top-7 opacity-0 group-hover:opacity-100 transition pointer-events-none bg-slate-900 text-white text-[10px] px-1.5 py-0.5 rounded shadow-md z-30 whitespace-nowrap">
                              {t.date}: {t.count} db
                            </div>
                            <div
                              className="w-full bg-indigo-500/80 hover:bg-indigo-600 rounded-t transition-all"
                              style={{ height: `${Math.max(4, pct)}%` }}
                            />
                          </div>
                        );
                      });
                    })()}
                  </div>
                )}
                <div className="flex justify-between items-center text-[10px] text-slate-400 mt-2 font-mono">
                  <span>{data.timeline[0]?.date || 'Kezdet'}</span>
                  <span>{data.timeline[data.timeline.length - 1]?.date || 'Ma'}</span>
                </div>
              </div>
            </div>

            {/* Óránkénti csúcsidőszak */}
            <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-5 shadow-xs space-y-3">
              <h3 className="text-sm font-bold text-slate-900 dark:text-white flex items-center justify-between">
                <span>Napszakos Használat (Óránként)</span>
                <Clock className="w-4 h-4 text-slate-400" />
              </h3>
              <p className="text-xs text-slate-500">Mely napszakokban dolgoznak leginkább a munkatársak?</p>

              <div className="grid grid-cols-6 gap-1 pt-2">
                {Array.from({ length: 24 }).map((_, hr) => {
                  const entry = data.hourly.find(h => h.hour === hr);
                  const count = entry?.count || 0;
                  const maxHr = Math.max(...data.hourly.map(h => h.count), 1);
                  const intensity = Math.round((count / maxHr) * 100);

                  return (
                    <div
                      key={hr}
                      className={`p-1.5 rounded-lg text-center border transition ${
                        count > 0
                          ? intensity > 60
                            ? 'bg-indigo-600 text-white border-indigo-700 font-bold'
                            : intensity > 20
                              ? 'bg-indigo-100 dark:bg-indigo-950/80 text-indigo-900 dark:text-indigo-200 border-indigo-300 dark:border-indigo-800'
                              : 'bg-slate-50 dark:bg-slate-800 text-slate-700 dark:text-slate-300 border-slate-200 dark:border-slate-700'
                          : 'bg-slate-50/50 dark:bg-slate-900/50 text-slate-400 border-transparent opacity-60'
                      }`}
                      title={`${hr}:00 - ${count} db művelet`}
                    >
                      <div className="text-[10px] font-mono">{hr}h</div>
                      <div className="text-[11px] font-bold mt-0.5">{count}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Rangsorok: Szervezetek és Felhasználók */}
      {data && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Legaktívabb Szervezetek */}
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-5 shadow-xs space-y-4">
            <div>
              <h3 className="text-sm font-bold text-slate-900 dark:text-white flex items-center gap-2">
                <Building2 className="w-4 h-4 text-purple-500" />
                <span>Legaktívabb Szervezetek & Kvóták</span>
              </h3>
              <p className="text-xs text-slate-500">Intézményenkénti aktivitás, valamint aktuális tárhely és rekordszám terhelés</p>
            </div>

            <div className="space-y-3 max-h-96 overflow-y-auto pr-1">
              {data.tenant_rankings.length === 0 ? (
                <div className="text-xs text-slate-400 py-6 text-center">Nincs szervezeti aktivitási adat.</div>
              ) : (
                data.tenant_rankings.map((tr, idx) => {
                  const storageUsedMb = (tr.storage_used_bytes / (1024 * 1024)).toFixed(1);
                  const storagePct = tr.max_storage_mb > 0 ? Math.round(((tr.storage_used_bytes / (1024 * 1024)) / tr.max_storage_mb) * 100) : 0;
                  const recordsPct = tr.max_records > 0 ? Math.round((tr.records_count / tr.max_records) * 100) : 0;

                  return (
                    <div key={tr.tenant_id} className="p-3.5 rounded-xl bg-slate-50 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-800 hover:border-purple-300 dark:hover:border-purple-800 transition">
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <div className="flex items-center gap-2 min-w-0">
                          <span className="w-5 h-5 rounded-full bg-purple-100 dark:bg-purple-950/70 text-purple-700 dark:text-purple-300 text-xs font-bold flex items-center justify-center shrink-0">
                            {idx + 1}
                          </span>
                          <div className="min-w-0">
                            <h4 className="text-xs font-bold text-slate-900 dark:text-white truncate">{tr.tenant_name}</h4>
                            <span className="text-[10px] font-mono text-slate-400">/{tr.tenant_slug} · Fő tevékenység: {tr.top_action}</span>
                          </div>
                        </div>
                        <span className="font-mono font-bold text-xs text-purple-700 dark:text-purple-300 bg-purple-50 dark:bg-purple-950/50 px-2.5 py-1 rounded-lg border border-purple-200 dark:border-purple-800 shrink-0">
                          {tr.action_count} művelet
                        </span>
                      </div>

                      {/* Kvóta kihasználtság minibar-ok */}
                      <div className="grid grid-cols-2 gap-3 pt-2 border-t border-slate-200/60 dark:border-slate-800 text-[10px]">
                        <div>
                          <div className="flex justify-between text-slate-500 mb-0.5">
                            <span className="flex items-center gap-1 font-medium"><HardDrive className="w-2.5 h-2.5" /> Tárhely:</span>
                            <span className="font-mono">{storageUsedMb} MB / {tr.max_storage_mb} MB</span>
                          </div>
                          <div className="w-full h-1 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
                            <div
                              className={`h-full rounded-full ${storagePct > 90 ? 'bg-rose-500' : storagePct > 75 ? 'bg-amber-500' : 'bg-emerald-500'}`}
                              style={{ width: `${Math.min(100, storagePct)}%` }}
                            />
                          </div>
                        </div>

                        <div>
                          <div className="flex justify-between text-slate-500 mb-0.5">
                            <span className="flex items-center gap-1 font-medium"><FileText className="w-2.5 h-2.5" /> Rekordok:</span>
                            <span className="font-mono">{tr.records_count} / {tr.max_records}</span>
                          </div>
                          <div className="w-full h-1 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
                            <div
                              className={`h-full rounded-full ${recordsPct > 90 ? 'bg-rose-500' : recordsPct > 75 ? 'bg-amber-500' : 'bg-indigo-500'}`}
                              style={{ width: `${Math.min(100, recordsPct)}%` }}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>

          {/* Legaktívabb Felhasználók */}
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-5 shadow-xs space-y-4">
            <div>
              <h3 className="text-sm font-bold text-slate-900 dark:text-white flex items-center gap-2">
                <Users className="w-4 h-4 text-blue-500" />
                <span>Legaktívabb Felhasználók</span>
              </h3>
              <p className="text-xs text-slate-500">Mely kollégák hajtják végre a legtöbb módosítást és kattintást?</p>
            </div>

            <div className="space-y-3 max-h-96 overflow-y-auto pr-1">
              {data.user_rankings.length === 0 ? (
                <div className="text-xs text-slate-400 py-6 text-center">Nincs felhasználói aktivitási adat.</div>
              ) : (
                data.user_rankings.map((ur, idx) => (
                  <div key={ur.user_id} className="p-3 rounded-xl bg-slate-50 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-800 hover:border-blue-300 dark:hover:border-blue-800 transition flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2.5 min-w-0">
                      <span className="w-5 h-5 rounded-full bg-blue-100 dark:bg-blue-950/70 text-blue-700 dark:text-blue-300 text-xs font-bold flex items-center justify-center shrink-0">
                        {idx + 1}
                      </span>
                      <div className="min-w-0">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span className="text-xs font-bold text-slate-900 dark:text-white">{ur.user_name}</span>
                          <span className="text-[10px] font-mono text-slate-400 truncate">({ur.user_email})</span>
                        </div>
                        <div className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5 truncate">
                          {ur.tenant_name} · Fő akció: <span className="font-semibold text-slate-700 dark:text-slate-300">{ur.top_action}</span>
                        </div>
                      </div>
                    </div>

                    <div className="text-right shrink-0">
                      <div className="font-mono font-bold text-xs text-blue-700 dark:text-blue-300">
                        {ur.action_count} művelet
                      </div>
                      <div className="text-[10px] text-slate-400 mt-0.5 font-mono">
                        {ur.last_active ? new Date(ur.last_active).toLocaleDateString('hu-HU') : ''}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
